"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import {
  Radio,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRightCircle,
  Inbox,
  Copy,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import type { Signal, PagedResult } from "@/lib/types";
import type { Source } from "@/lib/types/routing";
import type { SignalReceivedEvent, SignalDeliveryStatusChangedEvent } from "@/lib/types/signalr";
import { useSignalR } from "@/lib/hooks/use-signalr";
import { SourceTypeBadge } from "@/components/dashboard/source-type-badge";
import { EmptyState } from "@/components/dashboard/empty-state";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DeliveryDrawer } from "@/components/signals/delivery-drawer";
import { DocsLink } from "@/components/dashboard/docs-link";
import { statusChip, STATUS_TOKENS, type StatusTone } from "@/lib/status-tokens";

// ---------------------------------------------------------------------------
// Overall-status badge
// ---------------------------------------------------------------------------

function OverallStatusBadge({ status, overallStatusLabels }: { status: string | null; overallStatusLabels: Record<string, string> }) {
  const toneMap: Record<string, StatusTone> = {
    Received: "info",
    Rejected: "destructive",
    RoutedAll: "profit",
    RoutedPartial: "warning",
    RoutedNone: "destructive",
  };
  const display = status ?? "-";
  const cls = statusChip(toneMap[display] ?? "neutral");
  return (
    <Badge variant="outline" className={cls}>
      {overallStatusLabels[display] ?? display}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SignalsPage() {
  const t = useTranslations("dashboard.signals");
  const [signals, setSignals] = useState<Signal[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [symbolFilter, setSymbolFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const size = 20;

  // Source lookup map for enriching source names
  const [sourcesMap, setSourcesMap] = useState<Record<string, string>>({});

  // Drawer state
  const [drawerSignal, setDrawerSignal] = useState<Signal | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Real-time: prepend new signals (page 1 only)
  useSignalR("SignalReceived", (event: unknown) => {
    if (page !== 1) return;
    const e = event as SignalReceivedEvent;
    const stub: Signal = {
      id: e.signalId,
      licenseId: "",
      sourceId: e.sourceId,
      sourceType: "unknown",
      rawPayload: "",
      symbol: e.symbol,
      action: e.action,
      volume: e.volume,
      volumeType: null,
      parameters: {},
      status: e.overallStatus,
      overallStatus: e.overallStatus,
      errorMessage: null,
      latencyMs: null,
      executionTicket: null,
      executionPrice: null,
      executionSlippage: null,
      createdAt: e.receivedAt,
      processedAt: null,
      deliveredAt: null,
      executedAt: null,
    };
    // Only prepend if status filter allows it
    const passesFilter =
      statusFilter === "all" || statusFilter === e.overallStatus;
    if (passesFilter) {
      setSignals((prev) => [stub, ...prev].slice(0, size));
      setTotal((n) => n + 1);
    }
  });

  // Real-time: update overallStatus badge on the list row when a delivery resolves
  useSignalR("SignalDeliveryStatusChanged", (event: unknown) => {
    const e = event as SignalDeliveryStatusChangedEvent;
    // Update the list row's overall status if the drawer is closed or for a different signal
    setSignals((prev) =>
      prev.map((s) =>
        s.id === e.signalId
          ? {
              ...s,
              latencyMs: e.latencyMs ?? s.latencyMs,
              executionTicket: e.executionTicket ?? s.executionTicket,
              executionPrice: e.executionPrice ?? s.executionPrice,
            }
          : s
      )
    );
    // If the drawer is open for this signal, update drawerSignal so DeliveryDrawer can refetch
    setDrawerSignal((prev) => {
      if (prev?.id === e.signalId) {
        return {
          ...prev,
          latencyMs: e.latencyMs ?? prev.latencyMs,
          executionTicket: e.executionTicket ?? prev.executionTicket,
          executionPrice: e.executionPrice ?? prev.executionPrice,
        };
      }
      return prev;
    });
  });

  const openDrawer = useCallback((signal: Signal) => {
    setDrawerSignal(signal);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // Fetch sources once on mount
  useEffect(() => {
    api.sources
      .list()
      .then((sources: Source[]) => {
        const map: Record<string, string> = {};
        for (const s of sources) map[s.id] = s.name;
        setSourcesMap(map);
      })
      .catch(() => {
        // Non-fatal: fall back to sourceId truncation in the table
      });
  }, []);

  useEffect(() => {
    let ignore = false;
    async function load() {
      const params = new URLSearchParams({
        page: String(page),
        size: String(size),
      });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (symbolFilter.trim()) params.set("symbol", symbolFilter.trim());
      if (dateFrom) params.set("from", new Date(dateFrom).toISOString());
      if (dateTo) params.set("to", new Date(dateTo + "T23:59:59").toISOString());

      const data = await api.get<PagedResult<Signal>>(
        `/api/signals?${params.toString()}`
      );
      if (ignore) return;
      setSignals(data.items);
      setTotal(data.total);
    }
    void load();
    return () => {
      ignore = true;
    };
  }, [page, statusFilter, symbolFilter, dateFrom, dateTo]);

  const totalPages = Math.ceil(total / size);

  // Summary counts use overallStatus if present, fall back to legacy status
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const signal of signals) {
      const key = signal.overallStatus ?? signal.status;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [signals]);

  const statusSummary = [
    {
      label: "Received",
      count: statusCounts["Received"] || 0,
      icon: Inbox,
      color: STATUS_TOKENS.info.text,
    },
    {
      label: "RoutedAll",
      count: statusCounts["RoutedAll"] || 0,
      icon: CheckCircle2,
      color: STATUS_TOKENS.profit.text,
    },
    {
      label: "RoutedPartial",
      count: statusCounts["RoutedPartial"] || 0,
      icon: ArrowRightCircle,
      color: STATUS_TOKENS.warning.text,
    },
    {
      label: "Rejected",
      count: statusCounts["Rejected"] || 0,
      icon: AlertTriangle,
      color: STATUS_TOKENS.destructive.text,
    },
    {
      label: "RoutedNone",
      count: statusCounts["RoutedNone"] || 0,
      icon: XCircle,
      color: STATUS_TOKENS.destructive.text,
    },
  ];

  const overallStatusLabels: Record<string, string> = {
    Received: t("overallStatus.Received"),
    Rejected: t("overallStatus.Rejected"),
    RoutedAll: t("overallStatus.RoutedAll"),
    RoutedPartial: t("overallStatus.RoutedPartial"),
    RoutedNone: t("overallStatus.RoutedNone"),
  };

  const copySignalId = (id: string) => {
    navigator.clipboard.writeText(id);
    toast.success(t("idCopied"));
  };

  const getSourceLabel = (signal: Signal) => {
    if (signal.sourceId && sourcesMap[signal.sourceId]) {
      return sourcesMap[signal.sourceId];
    }
    if (signal.sourceId) {
      return signal.sourceId.slice(0, 8) + "…";
    }
    // Fall back to source-type badge if no sourceId
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <DocsLink href="/docs/reference/signal-format">{t("formatReference")}</DocsLink>
        <Button asChild size="sm">
          <Link href="/signals/manual">{t("sendManual")}</Link>
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <Select
          value={statusFilter}
          onValueChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-44">
            <SelectValue placeholder={t("filterPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all")}</SelectItem>
            <SelectItem value="Received">{t("overallStatus.Received")}</SelectItem>
            <SelectItem value="RoutedAll">{t("overallStatus.RoutedAll")}</SelectItem>
            <SelectItem value="RoutedPartial">{t("overallStatus.RoutedPartial")}</SelectItem>
            <SelectItem value="Rejected">{t("overallStatus.Rejected")}</SelectItem>
            <SelectItem value="RoutedNone">{t("overallStatus.RoutedNone")}</SelectItem>
          </SelectContent>
        </Select>
        <Input
          placeholder={t("symbolPlaceholder")}
          value={symbolFilter}
          onChange={(e) => {
            setSymbolFilter(e.target.value);
            setPage(1);
          }}
          className="w-40"
        />
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setPage(1);
            }}
            className="w-36"
            aria-label={t("dateFrom")}
          />
          <span className="text-sm text-muted-foreground">&ndash;</span>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setPage(1);
            }}
            className="w-36"
            aria-label={t("dateTo")}
          />
        </div>
      </div>

      {signals.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {statusSummary.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 border bg-card p-3 shadow-sm"
            >
              <item.icon className={`h-5 w-5 shrink-0 ${item.color}`} />
              <div>
                <p className="text-lg font-semibold leading-none">
                  {item.count}
                </p>
                <p className="text-xs text-muted-foreground">
                  {overallStatusLabels[item.label] ?? item.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {signals.length === 0 ? (
        <EmptyState
          icon={Radio}
          title={t("title")}
          description={t("empty")}
          action={{ label: t("emptyQuickstart"), href: "/docs/quickstart" }}
        />
      ) : (
        <div className="overflow-x-auto border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("columns.id")}</TableHead>
                <TableHead>{t("columns.time")}</TableHead>
                <TableHead>{t("columns.symbol")}</TableHead>
                <TableHead>{t("columns.action")}</TableHead>
                <TableHead>{t("columns.volume")}</TableHead>
                <TableHead>{t("columns.status")}</TableHead>
                <TableHead>{t("columns.source")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {signals.map((signal) => {
                const sourceLabel = getSourceLabel(signal);
                return (
                  <TableRow
                    key={signal.id}
                    className="cursor-pointer"
                    onClick={() => openDrawer(signal)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <code className="font-mono text-xs text-muted-foreground">
                          {signal.id.slice(0, 8)}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5"
                          onClick={(e) => {
                            e.stopPropagation();
                            copySignalId(signal.id);
                          }}
                          aria-label={t("copyId")}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Link
                        href={`/signals/${signal.id}`}
                        className="hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {new Date(signal.createdAt).toLocaleString()}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono font-medium">
                      {signal.symbol || (
                        <span className="text-xs font-normal text-muted-foreground">
                          {signal.rawPayload.length > 50
                            ? signal.rawPayload.slice(0, 50) + "..."
                            : signal.rawPayload}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{signal.action || "-"}</TableCell>
                    <TableCell>
                      {signal.volume ?? "-"}{" "}
                      {signal.volumeType && (
                        <span className="text-xs text-muted-foreground">
                          {signal.volumeType}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <OverallStatusBadge
                        status={signal.overallStatus ?? signal.status}
                        overallStatusLabels={overallStatusLabels}
                      />
                    </TableCell>
                    <TableCell>
                      {sourceLabel ? (
                        <span className="text-sm">{sourceLabel}</span>
                      ) : (
                        <SourceTypeBadge sourceType={signal.sourceType} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {t("showing")} {(page - 1) * size + 1}-
            {Math.min(page * size, total)} {t("of")} {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              {t("previous")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              {t("next")}
            </Button>
          </div>
        </div>
      )}

      {/* Per-delivery drawer */}
      <DeliveryDrawer
        signal={drawerSignal}
        open={drawerOpen}
        onClose={closeDrawer}
      />
    </div>
  );
}
